import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api, type CreateProblemCardBody } from '../lib/api'
import { getSessionId } from '../lib/session'
import { EMPTY_FORM, toProblemPayload, type ProblemFormValue } from '../lib/problemForm'
import { ProblemCardForm } from '../components/ProblemCardForm'

/**
 * 시민문제 등록 — 입력 UI는 `ProblemCardForm` 하나이고 **수정 화면이 같은 폼을 쓴다**
 * (09-04 합의). 이 페이지가 하는 일은 등록에만 있는 것 셋이다: 익명 세션 식별자를 싣고,
 * 성공하면 **수정 링크를 한 번 보여 주고**, 접수 안내를 그린다.
 *
 * **수정 링크는 이 응답에만 실린다** — 재발급 경로가 없다(개발플랜 §2 항목 3). 그래서
 * 완료 화면이 그 링크를 크게 보여 주고 복사 수단을 함께 둔다.
 */
export function ProblemNewPage() {
  const queryClient = useQueryClient()
  const [editPath, setEditPath] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: (body: CreateProblemCardBody) => api.createProblemCard(body),
    onSuccess: (res) => {
      setEditPath(res.editPath)
      void queryClient.invalidateQueries({ queryKey: ['problem-cards'] })
    },
  })

  const handleSubmit = (v: ProblemFormValue) => {
    const sessionId = getSessionId()
    mutation.mutate({
      ...toProblemPayload(v),
      // 익명 세션 식별자. 저장소를 못 쓰는 브라우저면 없이 보낸다 — 접수를 막지 않는다.
      ...(sessionId !== undefined ? { sessionId } : {}),
    })
  }

  // 접수완료 안내 — 과업지시서 §3-가가 요구한 화면이다(프로토타입에는 없었다).
  if (mutation.isSuccess) {
    return (
      <div className="panel">
        <h1 className="page-title">문제를 접수했습니다</h1>
        <p className="page-desc">
          등록하신 내용은 운영자 검토를 거친 뒤 공개됩니다. 등록 즉시 목록에 보이지는 않습니다.
        </p>
        {editPath !== null ? <EditLinkNotice path={editPath} /> : null}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link className="btn" to="/problems">
            시민문제카드 목록으로
          </Link>
          <button
            className="btn ghost"
            type="button"
            onClick={() => {
              setEditPath(null)
              mutation.reset()
            }}
          >
            하나 더 등록하기
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <Link className="back-link" to="/problems">
        ← 시민문제카드 목록
      </Link>
      <h1 className="page-title">문제 등록</h1>
      <p className="page-desc">
        동네에서 겪는 불편을 편한 말로 적어 주세요. 다듬어 쓰지 않아도 됩니다.
      </p>

      <div className="notice" style={{ marginBottom: 24 }}>
        운영기관이 연락드릴 수 있도록 <b>이름과 연락처를 함께 받습니다.</b> 연락처는{' '}
        <b>공개되지 않습니다.</b> 다만 적어 주신 문제 내용은 공개 화면과 지도에 실리므로, 본문에는
        다른 사람을 알아볼 수 있는 정보를 넣지 말아 주세요.
      </div>

      <ProblemCardForm
        mode="new"
        initial={EMPTY_FORM}
        submitLabel="등록하기"
        pendingLabel="등록하는 중…"
        isPending={mutation.isPending}
        error={mutation.error}
        onSubmit={handleSubmit}
      />
    </>
  )
}

/**
 * 수정 링크 안내 — **다시 발급되지 않는다.** 이 화면을 닫으면 시민이 자기 글을 고칠
 * 경로가 사라지므로, 링크를 눈에 보이게 적고 복사 버튼을 둔다.
 *
 * 서버가 준 것은 **경로**다(절대 URL 금지 — §4-13). 화면에는 지금 도메인을 붙여 보여
 * 준다. 클립보드를 못 쓰는 브라우저·비보안 컨텍스트가 있으므로 **글자 자체를 항상 함께
 * 보여 준다** — 복사 버튼만 두면 그 환경에서 링크를 잃는다.
 */
function EditLinkNotice({ path }: { path: string }) {
  const [copied, setCopied] = useState(false)
  const full = `${window.location.origin}${path}`

  return (
    <div className="notice" style={{ marginBottom: 16 }}>
      <b>내용을 고칠 수 있는 링크입니다. 지금 저장해 두세요.</b>
      <p className="hint" style={{ marginTop: 4 }}>
        이 링크는 다시 보여 드리지 않습니다. 링크를 가진 사람은 등록하신 내용과 연락처를 보고
        고칠 수 있으니 남에게 알려 주지 마세요.
      </p>
      <p style={{ wordBreak: 'break-all', margin: '8px 0' }}>
        <a href={path}>{full}</a>
      </p>
      <button
        className="btn ghost"
        type="button"
        onClick={() => {
          void navigator.clipboard
            ?.writeText(full)
            .then(() => setCopied(true))
            .catch(() => setCopied(false))
        }}
      >
        {copied ? '복사했습니다' : '링크 복사'}
      </button>
    </div>
  )
}
